import dom from "../../../dist/jsx-render/dom"
import { ITableDefinition } from "./ITableDefinition";
import { ColumnBase } from "./ColumnBase";
import { Settings } from "./Settings";
import { WaitPanelComponent } from "./WaitPanelComponent";
import { app } from "../../Frame/App";
import { PopupForm } from "../../Frame/PopupForm";
import { LockManager } from "../../Common/LockManager";
import { EventDispatcher } from "../../Events/EventDispatcher";
import { ITableViewData } from "./ITableViewData";
import { EditTypes } from "./EditTypes";
import { PopupCloseType, PagePopup } from "../PagePopup";
import { AttributesReader } from "../../Frame/AttributesReader";
import { Columns } from "./Columns";
import { ITableColumn } from "./ITableColumn";
import { FlexTableComponent } from "./FlexTableComponent";
import { FlexContentRowComponent } from "./FlexContentRowComponent";
import { ColumnSizeChangedData } from "./ColumnSizeChangedData";
import { AppDragTypes } from "../../Frame/AppDragTypes";
import { TableColumnTypes } from "./TableColumnTypes";
import { ColumnDropdown } from "./ColumnDropdown";
import { MeasureEditFactory } from "../../Pages/Measure/MeasureEditFactory";
import { IListItem } from "../../Model/IListItem";
import { ExcelData, ExcelColumn, ExcelRow } from "../../Model/ExcelData";
import { FlexTableRowItem } from "./FlexTableRowItem";
import { OutputTypeEnum } from "../../Areas/Admin/Reports/Models/OutputTypeEnum";
import { MsoTableInlineEdit } from "./MsoTableInlineEdit";
import { WidgetContextFe } from "../Widgets/Components/EditRow/AvailableWidgets/WidgetContextFe";
import { WidgetController } from "../Widgets/WidgetController";
import { ColumnFilter } from "./ColumnFilter";
import { TableColumnAlign } from "./TableColumnAlign";
import { ReportConnectionManager } from "../../Common/ReportConnectionManager";

declare var globalTableOnLoaded;

interface ResizeObserver {
    observe(element: HTMLElement): void;
}

interface ResizeObserverConstructor {
    new(callback: () => void): ResizeObserver;
    readonly prototype: ResizeObserver;
}

declare var ResizeObserver: ResizeObserverConstructor;

export type MsoTableGetDataSuccess = (columns: ITableColumn[],
    data: any[],
    settingsName: string,
    idField: string,
    singleLineDisplay?: boolean,
    resultLineDisplay?: boolean,
    newMeasureTemplateIds?: number[],
    rowClickUrl?: string,
    rowClickUrlUsePopup?: boolean,
    isSorted?: boolean,
    hideDuplicatesOption?: boolean
) => void;

export class InitializedEvent {
    element: HTMLElement;
    handler: any;
    type: string;
}

export class ReportModel {
    extention: string;
    measureId: number;
    measuresIds: string;
}

export type MsoTableProps = {
    loadUrl?: string;
    loadOnCreate: boolean;
    enableSort?: boolean;
    enablePaging?: boolean;
    rowsPerPage?: number;
    enableScroll?: boolean;
    modalFormName?: string;
    modalFormParams?: string;
    viewType?: string;
    singleTemplateId?: number;
    parentId?: number;
    parentGuid?: string;
    viewId?: number;
    defaultViewId?: number;
    editType?: EditTypes;
    popupUrl?: string;
    popupFullscreen?: boolean;
    onCloseEditPopup?: (reason: PopupCloseType, id: number) => void;
    hideHeader?: boolean;
    viewIndex?: number;
    settingsName?: string;
    idField?: string;
    fixedLayout?: boolean;
    fixedHeader?: boolean; // to be used with fixedLayout
    enableFilterPopup?: boolean;
    enableDragDrop?: boolean;
    posProperty?: string;
    editAdditionalAction?: string;
    headerUppercase?: boolean;
    addFilter?: boolean;
    addButtonNew?: boolean;
    showDeleteButton?: boolean;
    loadPartial?: boolean;
    enableFooter?: boolean;
    colorDataField?: string;
    enableCopyIcon?: boolean;
    copyEntityUrl?: string;
    enableExportIcon?: boolean;
    exportEntityUrl?: string;
    exportEntityType?: string;
    templateFieldId?: number;
    useMultiCategorySeparator?: boolean;
    expandAll?: boolean;
    isMeasureListField?: boolean;

    /** used full width of parent element, width of each cell is calculated automatically */
    flexCompleteWidth?: boolean;

    /** callbacks */
    rowClickCallback?: (ev: MouseEvent) => void;
    getCustomContent?: (field: string, model: any) => HTMLElement;
    onEditIconClicked?: (field: string, model: any) => void;
    onDeleteIconClicked?: (id: string) => void;
    onSearchIconClicked?: (id: string) => void;
    onCopyIconClicked?: (id: string) => void;
    onExportIconClicked?: (id: string) => void;
    onButtonClicked?: (id: string) => void;
    onCreateCustomHeaderRow?: (columns: Array<ColumnBase>) => HTMLTableRowElement;
    onCreateCustomSecondHeaderRow?: (columns: Array<ColumnBase>) => HTMLTableRowElement;
    onBeforeRowUpdate?: (model: any) => void;
    onRowUpdated?: (model: any) => void;
    onGetData?: (singleId: string, success: MsoTableGetDataSuccess) => void;
    onCreateChildTable?: (parentTable: MsoTable, id: number | string, rootElement: HTMLElement) => MsoTable;
    onCellClick?: (field: string, id: string | number) => void;
    onRowCreated?: (row: FlexContentRowComponent, model: any) => void;
    onModelChanged?: (field: string, model: any, originalField?: string) => void;
    onGetDataSource?: (field: string, source: string) => any[];
    onLoadComplete?: () => void;
    onBeforeCreateColumns?: (columns: ITableColumn[]) => void;
    getTextForNewButton?: (templateId: number) => string;

    /** url to be called on row click, {id} will be replaced with current row id */
    rowClickUrl?: string;
    rowClickUrlUsePopup?: boolean;
    rowClickAllowedUrl?: string;
    lazyLoadingAmount?: number;

    /** default SQL filter */
    defaultFilter?: string;
    topLevelFilter?: string;
    dataTemplateId?: number;
    editingScenario?: boolean;
};

export class MsoTable {
    public props: MsoTableProps;
    public settings: Settings = null;
    public useLock = false;
    public static allTables: { [id: string]: MsoTable } = {};

    private columns: Columns;
    public flexTable: FlexTableComponent;
    private columnsFromHtml = false;
    private dragCounter: number = 0;
    private userListForEdit: IListItem[];
    private buttonExcelExport: HTMLButtonElement;

    public static maxPartialRows: number = 200;
    private initializedEvents: InitializedEvent[];

    public get onRemoveColumnSort() {
        return this._onRemoveColumnSort.asEvent();
    }
    private _onRemoveColumnSort = new EventDispatcher<MsoTable, any>();

    public get onSetColumnSort() {
        return this._onSetColumnSort.asEvent();
    }
    private _onSetColumnSort = new EventDispatcher<string, boolean>();

    public get onChangeColumnFilter() {
        return this._onChangeColumnFilter.asEvent();
    }
    private _onChangeColumnFilter = new EventDispatcher<string, boolean>();

    public static get onColumnSizeChanged() {
        return this._onColumnSizeChanged.asEvent();
    }
    private static _onColumnSizeChanged = new EventDispatcher<MsoTable, ColumnSizeChangedData>();

    public static fireColumnSizeChanged(table: MsoTable, settingsName: string, columnIndex: number, width: string) {
        this._onColumnSizeChanged.dispatch(table, { settingsName: settingsName, columnIndex: columnIndex, width: width });
    }

    public static getTableById(id: string): MsoTable {
        return MsoTable.allTables[id];
    }

    public get rowCount(): number {
        return this.flexTable.rowCount;
    }

    public get columnCount(): number {
        return this.columns.items.length;
    }

    public getRootElement(): HTMLElement {
        return this.rootElement;
    }

    public get id(): string {
        return this.rootElement.id;
    }

    public get inlineEdit(): MsoTableInlineEdit {
        if (!this._inlineEdit) {
            this._inlineEdit = new MsoTableInlineEdit(this);
        }
        return this._inlineEdit;
    }
    private _inlineEdit: MsoTableInlineEdit;

    constructor(public rootElement: HTMLElement, props?: MsoTableProps) {

        this.initializedEvents = [];

        if (this.rootElement.id) {
            MsoTable.allTables[this.rootElement.id] = this;
        }

        // get parameters from rootElement
        if (props) {
            this.props = props;
        }
        else {
            const attr = new AttributesReader(rootElement);

            this.props = {
                loadUrl: attr.getString("load-url"),
                enableSort: attr.getBool("enable-sort"),
                enablePaging: attr.getBool("enable-paging"),
                rowsPerPage: attr.getNumber("rows-per-page"),
                enableScroll: attr.getBool("enable-scroll"),
                modalFormName: attr.getString("modal-form-name"),
                modalFormParams: attr.getString("modal-form-params"),
                viewIndex: attr.getNumber("view-index", 0),
                viewType: attr.getString("view-type", "None"),
                singleTemplateId: attr.getNumber("single-template-id", 0),
                parentId: attr.getNumber("parent-id", 0),
                parentGuid: attr.getString("parent-guid"),
                viewId: attr.getNumber("view-id"),
                defaultViewId: attr.getNumber("default-view-id"),
                editType: attr.getNumber("edit-type", EditTypes.None),
                popupUrl: attr.getString("popup-url"),
                popupFullscreen: attr.getBool("popup-fullscreen"),
                hideHeader: attr.getBool("hide-header"),
                loadOnCreate: attr.getBool("load-on-create", true),
                settingsName: attr.getString("settings-name"),
                idField: attr.getString("id-field", "id"),
                fixedLayout: attr.getBool("fixed-layout", false),
                fixedHeader: attr.getBool("fixed-header", false),
                enableFilterPopup: attr.getBool("enable-filter-popup", true),
                enableDragDrop: attr.getBool("enable-drag-drop", false),
                posProperty: attr.getString("pos-property"),
                editAdditionalAction: attr.getString("edit-additional-action"),
                headerUppercase: attr.getBool("header-uppercase", false),
                addFilter: attr.getBool("add-filter", false),
                addButtonNew: attr.getBool("add-button-new", false),
                flexCompleteWidth: attr.getBool("flex-complete-width", false),
                loadPartial: attr.getBool("load-partial", false),
                enableFooter: attr.getBool("enable-footer", false),
                rowClickUrl: attr.getString("row-click-url"),
                rowClickUrlUsePopup: attr.getBool("row-click-url-use-popup"),
                defaultFilter: attr.getString("default-filter"),
                topLevelFilter: attr.getString("top-level-filter"),
                rowClickAllowedUrl: attr.getString("row-click-allowed-url"),
                colorDataField: attr.getString("color-data-field"),
                enableCopyIcon: attr.getBool("enable-copy-icon", false),
                copyEntityUrl: attr.getString("copy-entity-url", ""),
                enableExportIcon: attr.getBool("enable-export-icon", false),
                exportEntityUrl: attr.getString("export-entity-url", ""),
                exportEntityType: attr.getString("export-entity-type", ""),
                showDeleteButton: attr.getBool("enable-delete-icon", false),
                templateFieldId: attr.getNumber("template-field-id", 0),
                expandAll: attr.getBool("expand-all", false),
                isMeasureListField: attr.getBool("is-measure-list-field", false)
            }

            this.props.dataTemplateId = attr.getNumber("data-template-id", 0);
            this.props.editingScenario = attr.getBool("data-template-editing-scenario", false);
        }


        // columns from HTML-content?
        let columnElement = this.rootElement.querySelector<HTMLElement>("columns");
        if (columnElement) {
            this.columns = new Columns();
            this.columns.createFromHtml(this, this.rootElement.id, columnElement);
            this.columnsFromHtml = true;
            this.rootElement.innerHTML = "";
        }

        if (this.props.enableScroll) {
            this.rootElement.classList.add("mt-scroll");
            document.body.classList.add("frame-overflow-hidden");
        }

        if (this.props.fixedLayout) {
            this.rootElement.classList.add("mt-fixed");
        }
        else {
            this.rootElement.classList.add("mt-flex");
        }

        if (this.props.enableDragDrop) {
            this.rootElement.classList.add("mt-drop");
        }

        if (this.props.headerUppercase) {
            this.rootElement.classList.add("mt-header-uppercase");
        }

        // register for drop
        if (this.props.enableDragDrop) {
            this.rootElement.addEventListener("drop", this.onDrop);
            this.rootElement.addEventListener("dragenter", this.onDragEnter);
            this.rootElement.addEventListener("dragleave", this.onDragLeave);
            this.rootElement.addEventListener("dragover", this.onDragOver);
        }

        if (this.props.enableScroll && this.rootElement.parentElement) {
            this.rootElement.parentElement.style.overflow = "hidden";
        }

        // show wait panel
        this.rootElement.appendChild(<WaitPanelComponent />);

        if (!this.rootElement.classList.contains("mso-table")) {
            this.rootElement.classList.add("mso-table");
        }

        // register on column size changed event
        MsoTable.onColumnSizeChanged.subscribe((sender: MsoTable, args: ColumnSizeChangedData) => {
            if (sender != this) {
                if (this.props.settingsName && this.props.settingsName == args.settingsName) {
                    this.flexTable.setColumnWidth(args.columnIndex, args.width);
                }
            }
        });

        if (this.props.editType == EditTypes.MeasureFullscreen || this.props.editType == EditTypes.MeasurePopup) {
            app.onNewMeasureCreated.subscribe((reason: PopupCloseType, id: number) => {
                if (id != 0) {
                    this.addRow(id.toString());
                }
            });
        }

        // load from server
        if (this.props.loadOnCreate) {
            this.load();
        }

    }

    public load(): void {
        this.getData("0", (columns: ITableColumn[],
            data: any[],
            settingsName: string,
            idField: string,
            singleLineDisplay: boolean,
            resultLineDisplay: boolean,
            newMeasureTemplateIds: number[],
            rowClickUrl: string,
            rowClickUrlUsePopup: boolean,
            isSorted: boolean,
            hideDuplicatesOption: boolean
        ) => {
            this.createAll(columns, data, settingsName, idField, singleLineDisplay, resultLineDisplay, newMeasureTemplateIds, rowClickUrl, rowClickUrlUsePopup, isSorted, hideDuplicatesOption);
            if (window.hasOwnProperty('globalTableOnLoaded')) { globalTableOnLoaded(this.id); }
        });
    }

    public clear(): void {
        this.rootElement.innerHTML = "";
        this.flexTable = null;
    }

    onDragOver = (ev: DragEvent) => {
        ev.preventDefault();
        return false;
    }

    onDrop = (ev: DragEvent) => {
        if (app.dragData && app.dragData.type == AppDragTypes.MsoTableRow) {
            this.rootElement.classList.add("mt-drop-animation");
            let row = app.dragData.sourceObject as FlexContentRowComponent;
            row.appendToContent();
            this.flexTable.recalcPos();
        }
    }

    onDragEnter = (ev: DragEvent) => {
        if (app.dragData && app.dragData.type == AppDragTypes.MsoTableRow) {
            this.dragCounter++;
            this.rootElement.classList.add("mt-drop-animation");
        }
    }

    onDragLeave = (ev: DragEvent) => {
        if (app.dragData && app.dragData.type == AppDragTypes.MsoTableRow) {
            this.dragCounter--;
            if (this.dragCounter <= 0) {
                this.rootElement.classList.remove("mt-drop-animation");
            }
        }
    }

    public createAll(columns: ITableColumn[],
        data: any[],
        settingsName: string = null,
        idField: string = null,
        singleLineDisplay: boolean = false,
        resultLineDisplay: boolean = false,
        newMeasureTemplateIds: number[] = [],
        rowClickUrl: string = null,
        rowClickUrlUsePopup: boolean = false,
        isSorted: boolean = false,
        hideDuplicatesOption: boolean = false,
        lazyLoadingAmount: number = 0) {

        this.clear();

        if (settingsName) {
            this.props.settingsName = settingsName;
        }
        if (idField) {
            this.props.idField = idField;
        }

        if (!this.props.enableFooter) {
            this.props.enableFooter = resultLineDisplay;
        }

        if (lazyLoadingAmount) {
            this.props.lazyLoadingAmount = lazyLoadingAmount;
        }

        if (this.props.enableExportIcon) {
            // If the copy icon option is enabled, a copy icon colum will be added to the column list.
            columns.unshift({
                type: TableColumnTypes.ExportIcon,
                toolTip: Resources.Common.export,
                number: 98,
                fixedWidth: (40 / window.devicePixelRatio) + "px",
                isFilterable: false,
                isSizeable: false,
                isEditable: false,
                isSortable: false,
                align: TableColumnAlign.Center
            });

            // The onExportIconClicked of the MsoTable is mapped to the exportEntity function.
            this.props.onExportIconClicked = (id: string) => {
                this.exportEntity(parseInt(id));
            }
        }

        if (columns && !this.columnsFromHtml) {
            if (this.props.enableCopyIcon) {
                // If the copy icon option is enabled, a copy icon colum will be added to the column list.
                columns.unshift({
                    type: TableColumnTypes.CopyIcon,
                    toolTip: Resources.Common.copy,
                    number: 99,
                    fixedWidth: (40 / window.devicePixelRatio) + "px",
                    isFilterable: false,
                    isSizeable: false,
                    isEditable: false,
                    isSortable: false,
                    align: TableColumnAlign.Center
                });

                // The onCopyIconClicked of the MsoTable is mapped to the copyEntity function.
                this.props.onCopyIconClicked = (id: string) => {
                    this.copyEntity(parseInt(id));
                }
            }

            this.columns = new Columns();
            this.columns.create(this, this.rootElement.id, columns);

            if (this.props.flexCompleteWidth) {
                const clientWidth = this.rootElement.clientWidth;
                this.columns.autoSize(clientWidth);
            }
        }

        if (!this.rootElement.hasAttribute("data-row-click-url")) {
            this.props.rowClickUrl = rowClickUrl;
        }

        if (!this.rootElement.hasAttribute("data-row-click-url-use-popup")) {
            this.props.rowClickUrlUsePopup = rowClickUrlUsePopup;
        }

        if (this.props.rowClickUrl) {
            this.props.editType = EditTypes.None;
        }

        this.settings = Settings.read(this.props.settingsName, this.columns);
        this.rootElement.innerHTML = "";

        const entityCtrl = document.querySelector("[entity-type]");
        if (entityCtrl) {
            let entityType = entityCtrl.getAttribute('entity-type');
            this.useLock = (app.lockManager as LockManager).getLockableInfo(entityType);

            // check if the list is of lockable entities.. if yes:
            if (this.useLock == true) {
                (app.lockManager as LockManager).watchEntityList(entityType, window);
            }
        }

        this.flexTable = new FlexTableComponent({
            table: this,
            columns: this.columns,
            enableSort: this.props.enableSort,
            settings: this.settings,
            models: data,
            editType: this.props.editType,
            idField: this.props.idField,
            fixedLayout: this.props.fixedLayout,
            fixedHeader: this.props.fixedHeader,
            enableFilterPopup: this.props.enableFilterPopup,
            enableScroll: this.props.enableScroll,
            enableDragDrop: this.props.enableDragDrop,
            posProperty: this.props.posProperty,
            addFilter: this.props.addFilter,
            addButtonNew: this.props.addButtonNew,
            showDeleteButton: this.props.showDeleteButton,
            flexCompleteWidth: this.props.flexCompleteWidth,
            enableFooter: this.props.enableFooter,
            hideDuplicatesOption: hideDuplicatesOption
        });
        this.rootElement.appendChild(this.flexTable.render());

        if (this.props.enableSort && !isSorted) {
            this.sort();
        } else {
            // remove current sort icon
            this._onRemoveColumnSort.dispatch(this, null);
        }

        if (this.props.onLoadComplete) {
            this.props.onLoadComplete();
        }

        if (this.props.addButtonNew) {
            this.addButtonNew(newMeasureTemplateIds);
        }

        if (this.props.flexCompleteWidth) {

            const ro = new ResizeObserver(this.autoSize).observe(this.rootElement);
        }

        if (singleLineDisplay)
            this.rootElement.classList.add("use-ellipsis");
        else
            this.rootElement.classList.remove("use-ellipsis");

        this.initReportButtons();

        // check columns for line brak
        if (this.columns && this.columns.items.length > 0) {
            this.columns.items.forEach(e => {
                this.flexTable.lineBreakColumn(e.index, e.width);
            });
        }
    }

    initReportButtons = () => {
        this.buttonExcelExport = document.getElementById("buttonExcelExport") as HTMLButtonElement;
        this.buttonExcelExport?.addEventListener("click", this.excelExport);
        this.initializedEvents.push({ element: this.buttonExcelExport, handler: this.excelExport, type: "click" });

        // button "Print": show report on click item
        const reports = document.getElementsByClassName("report-view-item");
        for (let i = 0; i < reports?.length; i++) {
            let handler = () => {
                this.showReport((reports.item(i) as HTMLLinkElement), parseInt(reports.item(i).getAttribute("data-id")), reports.item(i).innerHTML, parseInt(reports.item(i).getAttribute("data-output")));
            }
            reports.item(i).addEventListener("click", handler);
            this.initializedEvents.push({ element: reports.item(i) as HTMLElement, handler: handler, type: "click" });
        }

        // button "Export": Export to specific template on click item
        const exportTemplates = document.getElementsByClassName("export-view-item");
        for (let i = 0; i < exportTemplates?.length; i++) {
            let handler = () => {
                this.exportToTemplate(parseInt(exportTemplates.item(i).getAttribute("data-id")));
            }

            exportTemplates.item(i).addEventListener("click", handler);

            this.initializedEvents.push({ element: exportTemplates.item(i) as HTMLElement, handler: handler, type: "click" });
        }
    }

    // Create a request for the copy action of an entity by calling the copyEntityUrl
    copyEntity(entityId: number) {
        $.ajax({
            url: app.resolveUrl(this.props.copyEntityUrl.format(entityId)),
            type: "POST",
            contentType: "application/json",
            success: (result) => {
                if (result.success) {
                    this.addRowEntity(result.data.id, result.data);
                }
                console.log(result);
            },
            error: (err) => {
                console.log(err);
                alert(err);
            },
        });
    }

    addRowEntity(entityId: string, entityData: any) {
        let entity = this.createRowEntity(this.id, entityData);
        if (entity !== null) {
            this.addRow(entityId, entity);
        }
    }

    createRowEntity(table: string, data: any): any {
        if (app.isNullOrUndefined(table)) return null;

        switch (table) {
            case "templateList": {
                return {
                    "ErID": data.id,
                    "ErName": data.name,
                    "ErCreateUser": data.user,
                    "ErCreateDate": data.date
                };
            }
        }
    }

    // Create a request for the export action of an entity by calling the exportEntityUrl
    exportEntity(entityId: number) {
        $.ajax({
            url: app.resolveUrl(this.props.exportEntityUrl.format(entityId)),
            type: "POST",
            contentType: "application/json",
            success: (result) => {
                const blob = new Blob([result.file], { type: this.props.exportEntityType });
                const downloadUrl = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = downloadUrl;
                a.download = result.name;
                document.body.appendChild(a);
                a.click();
            },
            error: (err) => {
                console.log(err);
                alert(err);
            },
        });
    }

    showReport(item: HTMLLinkElement, id: number, title: string, output: OutputTypeEnum, externalFilter?: string, noPreloading?: boolean) {
        // get visible measures Ids in listview
        let measuresIds = this.getVisibleRowModelsIds();

        let tabId: string;
        let hiddenTabId = document.getElementById("tabId") as HTMLInputElement;
        if (hiddenTabId) {
            tabId = hiddenTabId.value;
        }

        switch (output) {
            case OutputTypeEnum.Vorschau:
                // Get column filters 
                let filter: string = "";
                let columnfilters = this.getListviewColumnFilter();
                for (let idx = 0; idx < columnfilters.length; idx++) {
                    if (filter != "") {
                        filter += "@" + columnfilters[idx].ToXml();
                    }
                    else {
                        filter = columnfilters[idx].ToXml();
                    }
                }

                let escapedExternalFilter: string;
                if (externalFilter) {
                    let regex = new RegExp("#", "g")
                    escapedExternalFilter = externalFilter.replace(regex, "&hash;");
                }

                let reportConnectionManager = new ReportConnectionManager();
                reportConnectionManager.init();
                reportConnectionManager.setReport(id, tabId);

                // define table data
                const viewdata: ITableViewData = {
                    view: this.props.viewType,
                    viewIndex: this.props.viewIndex,
                    singleId: "0",
                    singleTemplateId: this.props.singleTemplateId,
                    parentId: this.props.parentId,
                    viewId: this.props.viewId,
                    defaultViewId: this.props.defaultViewId,
                    defaultFilter: this.props.defaultFilter,
                    topLevelFilter: this.props.topLevelFilter,
                    columnFilter: filter,
                    externalFilter: escapedExternalFilter,
                    noPreloading: noPreloading,
                    dataTemplateId: this.props.dataTemplateId,
                    editingScenario: this.props.editingScenario,
                };

                console.log(viewdata);

                let data = JSON.stringify(viewdata);
                let encodedData = encodeURIComponent(data);
                let url = `~/Reports/StimulsoftViewer/${id}/0/${tabId}?viewData=${encodedData}`;
                console.log(url);

                new PagePopup({
                    url: app.resolveUrl(url),
                    title: title,
                    showCloseButton: true,
                    onClose: (reason: PopupCloseType, id: number) => {
                        reportConnectionManager.removeConnection();
                        return true;
                    }
                });
                break;
            case OutputTypeEnum.Pdf:
            case OutputTypeEnum.Xlsx:
                let model = new ReportModel();
                model.extention = OutputTypeEnum[output].toLowerCase();
                model.measureId = 0;
                model.measuresIds = JSON.stringify(measuresIds);
                this.downloadReport(id, model);
                break;
        }

    }

    downloadReport = (id: number, model: ReportModel) => {
        $.ajax({
            type: "POST",
            url: app.resolveUrl(`~/Admin/Reports/StimulsoftDesigner/downloadReport/${id}`),
            contentType: "application/json",
            data: JSON.stringify(model),
            xhrFields: {
                responseType: 'blob' // to avoid binary data being mangled on charset conversion
            },
            success: function (blob, status, xhr) {
                // check for a filename
                var filename = "";
                var disposition = xhr.getResponseHeader('Content-Disposition');
                if (disposition && disposition.indexOf('attachment') !== -1) {
                    var filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                    var matches = filenameRegex.exec(disposition);
                    if (matches != null && matches[1]) filename = matches[1].replace(/['"]/g, '');
                }

                if (typeof window.navigator.msSaveBlob !== 'undefined') {
                    // IE workaround for "HTML7007: One or more blob URLs were revoked by closing the blob for 
                    // which they were created.These URLs will no longer resolve as the data backing the URL has been freed."
                    window.navigator.msSaveBlob(blob, filename);
                } else {
                    var URL = window.URL || window.webkitURL;
                    var downloadUrl = URL.createObjectURL(blob);

                    if (filename) {
                        // use HTML5 a[download] attribute to specify filename
                        var a = document.createElement("a");
                        // safari doesn't support this yet
                        if (typeof a.download === 'undefined') {
                            window.location.href = downloadUrl;
                        } else {
                            a.href = downloadUrl;
                            a.download = filename;
                            document.body.appendChild(a);
                            a.click();
                        }
                    } else {
                        window.location.href = downloadUrl;
                    }

                    setTimeout(function () { URL.revokeObjectURL(downloadUrl); }, 100); // cleanup
                }
            },
            error: ((response) => {
                alert(response.statusText + ": " + response.responseText);
            })
        });
    }

    getListviewColumnFilter = (): ColumnFilter[] => {
        // get all columns with filter
        let filters: ColumnFilter[] = [];
        for (let col of this.columns.items) {
            if (col.filteredItems) {
                let model = new ColumnFilter();
                model.fieldName = col.field;
                model.operator = "=";
                if (col.type == TableColumnTypes.Date) {
                    model.dateValues = Object.keys(col.filteredItems);
                } else {
                    model.values = Object.keys(col.filteredItems)
                }
                filters.push(model);
            }
        }
        return filters;
    }


    getVisibleRowModelsIds = (): number[] => {
        let measuresIds: number[] = [];
        this.getVisibileRowModels().forEach(r => {
            measuresIds.push(r["Id"]);
        });
        return measuresIds;
    }


    exportToTemplate = (id: number) => {
        // get visible measures Ids in listview
        let measuresIds = this.getVisibleRowModelsIds();

        const viewData: ITableViewData = {
            view: this.props.viewType,
            viewIndex: this.props.viewIndex,
            singleId: "0",
            singleTemplateId: this.props.singleTemplateId,
            parentId: this.props.parentId,
            viewId: this.props.viewId,
            defaultViewId: this.props.defaultViewId,
            listeIds: measuresIds,
            defaultFilter: this.props.defaultFilter,
            topLevelFilter: this.props.topLevelFilter,
            dataTemplateId: this.props.dataTemplateId,
            editingScenario: this.props.editingScenario
        };

        $.ajax({
            type: "POST",
            url: app.resolveUrl(`~/api/tableView/getTemplateExcel/${id}`),
            data: JSON.stringify(viewData),
            contentType: "application/json",
            xhrFields: {
                responseType: 'blob' // to avoid binary data being mangled on charset conversion
            },
            success: function (blob, status, xhr) {
                // check for a filename
                var filename = "";
                var disposition = xhr.getResponseHeader('Content-Disposition');
                if (disposition && disposition.indexOf('attachment') !== -1) {
                    var filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                    var matches = filenameRegex.exec(disposition);
                    if (matches != null && matches[1]) filename = matches[1].replace(/['"]/g, '');
                }

                if (typeof window.navigator.msSaveBlob !== 'undefined') {
                    // IE workaround for "HTML7007: One or more blob URLs were revoked by closing the blob for 
                    // which they were created.These URLs will no longer resolve as the data backing the URL has been freed."
                    window.navigator.msSaveBlob(blob, filename);
                } else {
                    var URL = window.URL || window.webkitURL;
                    var downloadUrl = URL.createObjectURL(blob);

                    if (filename) {
                        // use HTML5 a[download] attribute to specify filename
                        var a = document.createElement("a");
                        // safari doesn't support this yet
                        if (typeof a.download === 'undefined') {
                            window.location.href = downloadUrl;
                        } else {
                            a.href = downloadUrl;
                            a.download = filename;
                            document.body.appendChild(a);
                            a.click();
                        }
                    } else {
                        window.location.href = downloadUrl;
                    }

                    setTimeout(function () { URL.revokeObjectURL(downloadUrl); }, 100); // cleanup
                }
            },
            error: ((response) => {
                alert(response.statusText + ": " + response.responseText);
            })
        });

    }

    isInResize: boolean = false;

    autoSize = () => {
        if (!this.isInResize) {
            this.isInResize = true;
            const clientWidth = this.rootElement.clientWidth;
            this.columns.autoSize(clientWidth);
            for (let i = 0; i < this.columns.items.length; i++) {
                this.flexTable.setColumnWidth(i, this.columns.items[i].width + "px");
            }
            this.isInResize = false;
        }
    };

    public addRow(id: string, model: any = null): FlexContentRowComponent {
        let result: FlexContentRowComponent = null;
        if (model) {
            if (this.props.onBeforeRowUpdate) {
                this.props.onBeforeRowUpdate(model);
            }
            result = this.flexTable.addRow(model);
            if (this.props.onRowUpdated) {
                this.props.onRowUpdated(model);
            }
        }
        else {
            this.getData(id, (columns: ITableColumn[], data: any[], settingsName: string, idField: string, singleLineDisplay: boolean) => {
                if (data && data.length > 0) {
                    if (this.props.onBeforeRowUpdate) {
                        this.props.onBeforeRowUpdate(data[0]);
                    }
                    result = this.flexTable.addRow(data[0]);
                    if (this.props.onRowUpdated) {
                        this.props.onRowUpdated(data[0]);
                    }
                }
            });
        }
        return result;
    }

    public updateRowFromModel(model: any, fireEvent: boolean = true) {
        if (this.props.onBeforeRowUpdate) {
            this.props.onBeforeRowUpdate(model);
        }
        this.flexTable.updateRow(model);
        if (fireEvent && this.props.onRowUpdated) {
            this.props.onRowUpdated(model);
        }
    }

    public updateRow(id: string, onUpdateDone?: () => void) {
        this.getData(id, (columns: ITableColumn[], data: any[], settingsName: string, idField: string, singleLineDisplay: boolean) => {
            if (data && data.length > 0) {
                const model = data[0];
                this.updateRowFromModel(model);
            }
            //else {
            //    this.load();
            //}
            if (onUpdateDone) {
                onUpdateDone();
            }
        });
    }

    public removeRow(id: string) {
        this.flexTable.removeRow(id);
    }

    private getData(singleId: string, success: MsoTableGetDataSuccess): void {
        if (this.props.onGetData) {
            this.props.onGetData(singleId, success);
        }
        else if (this.props.loadUrl) {
            // create server object
            const data: ITableViewData = {
                view: this.props.viewType,
                viewIndex: this.props.viewIndex,
                singleId: singleId,
                singleTemplateId: this.props.singleTemplateId,
                parentId: this.props.parentId,
                viewId: this.props.viewId,
                defaultViewId: this.props.defaultViewId,
                defaultFilter: this.props.defaultFilter,
                topLevelFilter: this.props.topLevelFilter,
                currentDateTime: new Date(),
                isMeasureListField: this.props.isMeasureListField,
                dataTemplateId: this.props.dataTemplateId,
                editingScenario: this.props.editingScenario
            };

            $.ajax({
                type: "POST",
                url: app.resolveUrl(this.props.loadUrl),
                dataType: 'json',
                contentType: "application/json; charset=utf-8",
                data: JSON.stringify(data),
                //async: false,
                success: (def: ITableDefinition) => {
                    success(def.columns, def.data, def.settingsName, def.idField, def.singleLineDisplay, def.resultLineDisplay, def.newMeasureTemplateIds, def.rowClickUrl, def.rowClickUrlUsePopup, def.isSorted, def.hideDuplicatesOption);
                },
                error: ((response) => {
                    alert(response.statusText + ": " + response.responseText);
                })
            });
        }
    }

    public hasRowClick(): boolean {
        if (this.props.rowClickCallback || this.props.popupUrl || this.props.modalFormName || this.props.rowClickUrl) {
            return true;
        }
        return false;
    }

    public onRowClick = (ev: MouseEvent) => {
        if (this.props.rowClickCallback) {
            this.props.rowClickCallback(ev);
        }

        if (this.props.popupUrl) {
            const id = (ev.currentTarget as HTMLElement).getAttribute("data-id");
            app.showPopup(this.props.popupUrl + "?id=" + id, parseInt(id), this.props.popupFullscreen, this.rootElement.id);
        }
        else if (this.props.modalFormName) {
            const id = (ev.currentTarget as HTMLElement).getAttribute("data-id");
            const popup = PopupForm.show(this.props.modalFormName, this.props.modalFormParams.replace("{0}", id.toString()));
            popup.onClosed.subscribe(() => {
                this.updateRow(id);
            });
        }
    }

    public sortColumnClicked(field: string, asc: boolean, isRemoved: boolean = false) {
        if (!this.props.enableSort) {
            return;
        }

        // remove current sort icon
        this._onRemoveColumnSort.dispatch(this, null);

        if (isRemoved) {
            // delete the current clicked sort when click on "sortierung aufheben" 
            this.settings.delete(field, this.props.settingsName);
            this.load();

        } else {
            this.settings.sortField = field;
            this.settings.sortAsc = asc;
            this.settings.write(this.props.settingsName);

            // show icon
            this._onSetColumnSort.dispatch(field, asc);
            this.sort();
        }

    }

    public sort() {

        const field = this.settings.sortField;
        const asc = this.settings.sortAsc;


        let sortColumn = this.columns.findByField(field);
        if (sortColumn) {
            // build array with all root rows
            let arrSort: Array<FlexTableRowItem> = [];
            for (let item of this.flexTable.getRowItems()) {
                arrSort.push(item);
            }

            // sort root items
            arrSort.sort((a: FlexTableRowItem, b: FlexTableRowItem): number => {
                return sortColumn.compare(a.model, b.model, asc);
            });

            if (this.props.loadPartial) {
                this.flexTable.replaceRowItems(arrSort);
                this.flexTable.showPartialContent();
            }
            else {
                for (let item of arrSort) {
                    item.row.appendToContent();
                }
            }
        }
    }

    public getRowComponent(id: string): FlexContentRowComponent {
        let items = this.flexTable.findRowsById(id);
        if (items.length > 0) {
            return items[0].row;
        }
        return null;
    }

    public getRowComponentByElementId(id: string): FlexContentRowComponent {
        return this.flexTable.findRowsByElementId(id);
    }


    public getRowModel(id: string) {
        let items = this.flexTable.findRowsById(id);
        if (items.length > 0) {
            return items[0].model;
        }
        return null;
    }

    public getAllRowModels(): Array<any> {
        let result = new Array<any>();
        for (let item of this.flexTable.getRowItems()) {
            result.push(item.model);
        }
        return result;
    }

    public getVisibileRowModels(): Array<any> {
        let result = new Array<any>();
        for (let item of this.flexTable.getRowItems()) {
            if (item.isVisible) {
                result.push(item.model);
            }
        }
        return result;
    }

    public fireChangeColumnFilter(field: string, on: boolean) {
        this._onChangeColumnFilter.dispatch(field, on);
    }

    public filter() {
        // get all columns with filter
        let filterCols: Array<ColumnBase> = [];
        for (let col of this.columns.items) {
            if (col.filteredItems) {
                filterCols.push(col);
            }
        }
        if (filterCols.length > 0) {
            for (let item of this.flexTable.getRowItems()) {
                item.show();
                for (let col of filterCols) {
                    if (!col.isFilterVisible(item.model)) {
                        item.hide();
                        break;
                    }
                }
            }

            if (this.props.loadPartial) {
                this.flexTable.showPartialContent();
            }
        }
        else {
            // show all rows
            for (let item of this.flexTable.getRowItems()) {
                item.show();
            }

            if (this.props.loadPartial) {
                this.flexTable.showPartialContent();
            }
        }
        this.flexTable.updateFooter();
    }

    public storeNewWidth(field: string, columnIndex: number, newWidth: number): void {
        this.settings.setWidth(field, Math.round(newWidth * window.devicePixelRatio));
        this.settings.write(this.props.settingsName);
        if (this.props.settingsName) {
            MsoTable.fireColumnSizeChanged(this, this.props.settingsName, columnIndex, Math.round(newWidth * window.devicePixelRatio) + "px");
        }
    }

    public recreateDropdown(field: string, id: string) {
        let column = this.columns.findByField(field);
        if (column && column.type == TableColumnTypes.Dropdown) {
            let items = this.flexTable.findRowsById(id);
            for (let item of items) {
                if (item.row) {
                    (column as ColumnDropdown).recreate(item.row.cells[column.index], item.model);
                }
            }
        }
    }

    public showEditPopup(editType: EditTypes, model: any): void {
        if (editType == EditTypes.MeasureFullscreen || editType == EditTypes.MeasurePopup) {
            MeasureEditFactory.showMeasure({
                measureId: model[this.props.idField],
                templateId: 0,
                parentId: 0,
                parentGuid: null,
                fullScreen: (editType == EditTypes.MeasureFullscreen),
                additionalAction: this.props.editAdditionalAction,
                onClose: (reason: PopupCloseType, id: number) => {
                    // update all tables
                    for (let tableId in MsoTable.allTables) {
                        let table = MsoTable.allTables[tableId];
                        if ((table.props.editType == EditTypes.MeasureFullscreen || table.props.editType == EditTypes.MeasurePopup) && this.props.loadUrl) {
                            if (reason == "delete") {
                                table.removeRow(id.toString());
                            }
                            else {
                                table.updateRow(id.toString());
                            }
                        }
                    }

                    if (this.props.onCloseEditPopup) {
                        this.props.onCloseEditPopup(reason, id);
                    }

                    // Recreate all widgets if exist
                    let frame = document.getElementById("widgetFrame");
                    if (frame) {
                        frame.innerHTML = "";
                        let controller = new WidgetController("widgetFrame", "cockpitWidgetsEdit", new WidgetContextFe());
                    }



                }
            });
        }
    }


    public getUserListForEdit(): JQueryPromise<IListItem[]> {
        return $.Deferred((deferrer) => {
            if (this.userListForEdit) {
                deferrer.resolve(this.userListForEdit);
            }
            $.getJSON(app.resolveUrl("~/api/users/getList"))
                .done((data: IListItem[]) => {
                    this.userListForEdit = data;
                    deferrer.resolve(this.userListForEdit);
                })
                .fail((res) => {
                    deferrer.reject(res);
                });
        }).promise();
    }

    private addButtonNew = (newMeasureTemplateIds: number[]) => {

        if (newMeasureTemplateIds.length > 0) {
            for (let id of newMeasureTemplateIds) {
                this.addButtonNewForTemplateId(id);
            }
        } else if (this.props.singleTemplateId) {
            this.addButtonNewForTemplateId(this.props.singleTemplateId);
        } else {
            // No template id for new measure available - do nothing
        }
    }

    private addButtonNewForTemplateId = (templateId: number) => {
        let text = Resources.Common.newMeasure;

        if (this.props.getTextForNewButton) {
            text = this.props.getTextForNewButton(templateId);
        }

        const buttonLink = (
            <a href="#" className="btn btn-custom btn-mso" data-template-id={templateId}>
                <i className="fas fa-plus mr-2"></i>{text}
            </a>
        );

        buttonLink.addEventListener("click", () => {
            MeasureEditFactory.showMeasure({
                measureId: 0,
                templateId: templateId,
                parentId: this.props.parentId ? this.props.parentId : 0,
                fullScreen: true,
                parentGuid: this.props.parentGuid && this.props.parentGuid.length > 0 ? this.props.parentGuid : "",
                onClose: (reason: PopupCloseType, id: number) => {
                    if (reason == "delete") {
                    }
                    else if (reason == "save") {
                        this.addRow(id.toString());
                    }
                }
            })
        });

        const buttonNew =
            <div className="button-new text-right">
                {buttonLink}
            </div>

        this.rootElement.appendChild(buttonNew);
    }

    private excelExport = (event: MouseEvent) => {
        // get all visible rows
        let excelData = new ExcelData();

        const viewName = (document.querySelector("#gridselectMeasures button[data-view-name]") as HTMLButtonElement)?.getAttribute("data-view-name");
        excelData.name = viewName;

        excelData.columns = [];

        for (let col of this.columns.items) {
            if (col.type != TableColumnTypes.Expand) {
                let excelColumn: ExcelColumn;
                if (excelData.columns.filter(ec => ec.name == col.headerText).length > 0) {
                    let name = col.headerText;
                    let addition = 1;

                    while (excelData.columns.filter(ec => ec.name == name).length > 0) {
                        name = `${name}_${addition++}`;
                    }

                    excelColumn = { name: name, type: col.type, field: col.field };
                } else {
                    excelColumn = { name: col.headerText, type: col.type, field: col.field };
                }
                excelData.columns.push(excelColumn);
            }
        }

        this.rootElement.querySelectorAll(".mt-content-row").forEach(r => {
            if (app.isVisible(r as HTMLElement)) {
                const idString = r.getAttribute("data-id");
                var rowsById = this.flexTable.findRowsById(idString);
                let row: any;

                if (rowsById.length > 0) {
                    row = rowsById[0].model;
                }

                if (row) {
                    let excelRow: ExcelRow = {};

                    for (let column of excelData.columns) {
                        if (column.type === TableColumnTypes.Picture) {
                            if (column.field.startsWith("LF#")) {
                                let fieldId = parseInt(column.field.substr(3));
                                excelRow[column.name] = JSON.stringify({ "rowId": row.Id, "fieldId": fieldId });
                            }
                        }
                        else {
                            excelRow[column.name] = row[column.field];
                        }
                    }

                    excelData.rows.push(excelRow);
                }
            }
        });

        $.ajax({
            type: "POST",
            url: app.resolveUrl("~/api/tableView/getExcel"),
            data: JSON.stringify(excelData),
            contentType: "application/json",
            xhrFields: {
                responseType: 'blob' // to avoid binary data being mangled on charset conversion
            },
            success: function (blob, status, xhr) {
                // check for a filename
                var filename = "";
                var disposition = xhr.getResponseHeader('Content-Disposition');
                if (disposition && disposition.indexOf('attachment') !== -1) {
                    var filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                    var matches = filenameRegex.exec(disposition);
                    if (matches != null && matches[1]) filename = matches[1].replace(/['"]/g, '');
                }

                if (typeof window.navigator.msSaveBlob !== 'undefined') {
                    // IE workaround for "HTML7007: One or more blob URLs were revoked by closing the blob for 
                    // which they were created.These URLs will no longer resolve as the data backing the URL has been freed."
                    window.navigator.msSaveBlob(blob, filename);
                } else {
                    var URL = window.URL || window.webkitURL;
                    var downloadUrl = URL.createObjectURL(blob);

                    if (filename) {
                        // use HTML5 a[download] attribute to specify filename
                        var a = document.createElement("a");
                        // safari doesn't support this yet
                        if (typeof a.download === 'undefined') {
                            window.location.href = downloadUrl;
                        } else {
                            a.href = downloadUrl;
                            a.download = filename;
                            document.body.appendChild(a);
                            a.click();
                        }
                    } else {
                        window.location.href = downloadUrl;
                    }

                    setTimeout(function () { URL.revokeObjectURL(downloadUrl); }, 100); // cleanup
                }
            }
        });
    }

    public unloadEvents = () => {
        for (let item of this.initializedEvents) {
            if (item.element) {
                item.element.removeEventListener(item.type, item.handler);
            }
        }
    }

    /** This command will remove the table from the dom, tries to dispose of the MsoTable object and recreates it with the same settings.
     * It will return the new instance of the table. All other references to the disposed table should be removed before calling this method
     * to ensure that the table will be removed from memory. Reattach externally added event handlers afterwards.
     */
    public static rebuild = (rootElement: HTMLElement): MsoTable => {
        // destroy existing HTML
        app.destroyDomChildren(rootElement);

        MsoTable.allTables[rootElement.id] = null;
        delete MsoTable.allTables[rootElement.id];

        let newTable = new MsoTable(rootElement);
        MsoTable.allTables[rootElement.id] = newTable;
        return newTable;
    }

    public resetColumnSizes = () => {
        window.localStorage.removeItem(this.props.settingsName);

        for (let i = 0; i < this.columns.items.length; i++) {
            let col = this.columns.items[i];
            col.resetWidth();

            let width = col.calculateWidth();

            if (!this.props.fixedLayout && col.width) {
                width = Math.round(col.width / window.devicePixelRatio) + "px";
            }

            this.flexTable.setColumnWidth(i, width);
        }
    }

    public setHeaderColor = (color: string) => {
        let headerCells = this.rootElement.querySelectorAll(".mt-header-inner .mt-cell");
        if (headerCells) {
            headerCells.forEach(h => (h as HTMLElement).style.backgroundColor = color);
        }
    }
}
